import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from './MobileApp';
import { useSettings } from '../../context/SettingsContext';
import { mobileApi } from '../../services/api';
import MobileNav from '../../components/mobile/MobileNav';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileCard from '../../components/mobile/MobileCard';
import { CheckCircle2, Loader2, CalendarClock, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const MobileServicesDue = () => {
  const navigate = useNavigate();
  const { getToken } = useMobileAuth();
  const { formatDate } = useSettings();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [claiming, setClaiming] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await mobileApi.getMaintenanceDue(getToken());
      setItems(r.data || []);
    } catch { toast.error('Failed to load services'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const claim = async (item) => {
    if (item.claimed) { navigate(`/mobile/work-orders/${item.claimed.work_order_id}`); return; }
    setClaiming(item.schedule.id);
    try {
      const r = await mobileApi.claimMaintenance(item.schedule.id, getToken());
      const woId = r.data?.work_order_id;
      if (r.data?.status === 'exists') toast.info(`Already on ${r.data.reference}`);
      else toast.success(`Work order ${r.data?.reference} created`);
      if (woId) navigate(`/mobile/work-orders/${woId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not claim service');
    } finally { setClaiming(null); }
  };

  const dueLabel = (d) => {
    if (d.status === 'overdue') {
      if (d.overdue_days) return `Overdue ${d.overdue_days} ${d.overdue_days === 1 ? 'day' : 'days'}`;
      if (d.hours_remaining != null && d.hours_remaining <= 0) return `Overdue ${Math.abs(Math.round(d.hours_remaining))} hrs`;
      return 'Overdue';
    }
    if (d.next_due_hours != null && d.hours_remaining != null) return `Due in ${Math.round(d.hours_remaining)} hrs`;
    if (d.next_due_date) return `Due ${formatDate(d.next_due_date)}`;
    return 'Due soon';
  };

  const overdue = items.filter((i) => i.due.status === 'overdue');
  const dueSoon = items.filter((i) => i.due.status === 'due_soon');

  const Row = ({ item }) => {
    const a = item.asset;
    const od = item.due.status === 'overdue';
    const model = [a.make, a.model].filter(Boolean).join(' ');
    const isClaiming = claiming === item.schedule.id;
    return (
      <MobileCard spine={od ? 'var(--overdue)' : 'var(--volt-edge)'} className="mb-2.5" padding="normal"
        data-testid={`service-row-${item.schedule.id}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="font-mono text-[14px] font-bold text-[var(--ink-primary)] shrink-0">{a.unit_number || a.name}</span>
              {model && <span className="text-[13px] text-[var(--ink-secondary)] truncate">{model}</span>}
            </div>
            <div className="text-[13px] text-[var(--ink-secondary)] truncate mt-0.5">{item.schedule.name}</div>
            <div className="flex items-center gap-1.5 mt-1.5 text-[13px]">
              <span className="shrink-0 rounded-full" style={{ width: 6, height: 6, background: od ? 'var(--overdue)' : 'var(--volt-edge)' }} />
              <span className={`font-semibold ${od ? 'text-[var(--overdue-ink)]' : 'text-[var(--ink-secondary)]'}`}>{dueLabel(item.due)}</span>
            </div>
            {item.claimed && (
              <div className="text-[12px] text-[var(--ink-tertiary)] mt-1">Claimed by {item.claimed.assigned_name || 'a colleague'} · {item.claimed.reference}</div>
            )}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); claim(item); }}
            disabled={isClaiming}
            data-testid={`service-claim-${item.schedule.id}`}
            className="shrink-0 h-11 px-4 rounded-[12px] text-[13px] font-bold flex items-center gap-1.5"
            style={item.claimed
              ? { background: 'white', color: 'var(--ink-primary)', border: '1.5px solid var(--border-strong)' }
              : { background: 'var(--volt-edge)', color: 'var(--volt-ink, #1A1A1A)' }}
          >
            {isClaiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
            {item.claimed ? 'View WO' : 'Claim'}
          </button>
        </div>
      </MobileCard>
    );
  };

  const SectionHeader = ({ label, count, tone }) => (
    <div className="flex items-center gap-3 mb-3 mt-1">
      <span className="text-[11px] font-bold uppercase tracking-[0.11em]" style={{ color: tone === 'overdue' ? 'var(--overdue-ink)' : '#6E6A61' }}>{label}</span>
      <span className={`font-mono text-[11px] px-1.5 py-0.5 rounded-[4px] ${tone === 'overdue' ? 'bg-[var(--overdue-bg)] text-[var(--overdue-ink)]' : 'bg-[var(--surface-sunken)] text-[var(--ink-tertiary)]'}`}>{count}</span>
      <div className="h-px flex-1 bg-[var(--border)]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--surface-page)] pb-24">
      <MobileHeader title="Services Due" showBack />
      <div className="px-4 py-4">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[var(--volt-icon)] animate-spin" /></div>
        ) : items.length === 0 ? (
          <MobileCard className="text-center py-8" data-testid="services-empty">
            <CheckCircle2 className="w-10 h-10 text-[var(--pass-ink)] mx-auto mb-2" />
            <p className="text-[var(--ink-tertiary)]">No services due right now</p>
          </MobileCard>
        ) : (
          <>
            {overdue.length > 0 && (<><SectionHeader label="Overdue" count={overdue.length} tone="overdue" />{overdue.map((it) => <Row key={it.schedule.id} item={it} />)}</>)}
            {dueSoon.length > 0 && (<><SectionHeader label="Due soon" count={dueSoon.length} tone="neutral" />{dueSoon.map((it) => <Row key={it.schedule.id} item={it} />)}</>)}
            <div className="flex items-center justify-center gap-1.5 mt-4 text-[12px] text-[var(--ink-tertiary)]">
              <CalendarClock className="w-3.5 h-3.5" /> Claiming a service creates a work order assigned to you
            </div>
          </>
        )}
      </div>
      <MobileNav />
    </div>
  );
};

export default MobileServicesDue;
